import { Injectable } from '@angular/core';

import { environment } from 'src/environments/environment';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import { Location } from '@angular/common';
import { isRenderingForMobileApp } from 'src/constants';
import { Router } from '@angular/router';
import { TrxService } from '~services/trx/trx.service';
import { GetIdentityProviderResponse, IdentityClaims, IdentityProvider } from '~models';
import { CookieService } from 'ngx-cookie-service';
import { HttpClient } from '@angular/common/http';
import * as moment from 'moment';
import { FeatureFlagService } from '~services/feature-flag/feature-flag.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public customerId: string;
  private identityProvider = IdentityProvider.IdentityServer4;
  private config: AuthConfig;

  constructor(
    private oauthService: OAuthService,
    private location: Location,
    private trxService: TrxService,
    private cookieService: CookieService,
    private router: Router,
    private ngHttp: HttpClient,
    private featureFlagService: FeatureFlagService,
  ) {}

  async initialize() {
    await this.initIdentityProvider();
    this.initConfig();
    this.oauthService.configure(this.config);
    await this.oauthService.loadDiscoveryDocumentAndTryLogin();
    this.setCustomerId(); // required for initial login after loadDiscoveryDocumentAndTryLogin
    this.oauthService.setupAutomaticSilentRefresh();

    if (!this.oauthService.hasValidAccessToken() && isRenderingForMobileApp) {
      return this.oauthService.initLoginFlow(this.location.path());
    }

    await this.trxService.setCustomerId(this.customerId);

    if (
      this.oauthService.state &&
      this.oauthService.state !== 'undefined' &&
      this.oauthService.state !== 'null'
    ) {
      let stateUrl = this.oauthService.state;
      if (stateUrl.startsWith('/') === false) {
        stateUrl = decodeURIComponent(stateUrl);
      }
      await this.router.navigateByUrl(stateUrl);
    }
  }

  private saveZipIdCookie() {
    if (this.customerId) {
      const cookieOptions = {
        domain: `.${window.location.hostname}`,
        path: '/',
        secure: true,
        expires: moment().add({ years: 1 }).toDate(),
      };
      this.cookieService.set('zip_id', this.customerId, {
        ...cookieOptions,
      });
    }
  }

  public setCustomerId() {
    if (this.oauthService.hasValidIdToken()) {
      const claims = this.oauthService.getIdentityClaims() as IdentityClaims;
      this.customerId = claims?.customer_id ?? claims?.sub?.replace('auth0|', '');
    } else {
      this.customerId = this.cookieService.get('zip_id');
    }
    this.saveZipIdCookie();
  }

  private setIdentityProvider(identityProvider: IdentityProvider) {
    this.identityProvider = identityProvider;

    const cookieOptions = {
      domain: `.${window.location.hostname}`,
      path: '/',
      secure: true,
      expires: moment().add({ years: 1 }).toDate(),
    };

    this.cookieService.set('idp_cookie', this.identityProvider, {
      ...cookieOptions,
    });
    sessionStorage.setItem('identityProvider', this.identityProvider);
  }

  private setIdentityProviderFromIssuer() {
    const issuer = (this.oauthService.getIdentityClaims() as IdentityClaims)?.iss;
    if (issuer === `${environment.zipAuth0.issuer}/`) {
      return this.setIdentityProvider(IdentityProvider.Okta);
    }
    return this.setIdentityProvider(IdentityProvider.IdentityServer4);
  }

  private async initIdentityProvider() {
    const idpCookie = this.cookieService.get('idp_cookie') as IdentityProvider;

    if (isRenderingForMobileApp) {
      return this.setIdentityProvider(idpCookie ?? IdentityProvider.IdentityServer4);
    }

    if (this.oauthService.hasValidAccessToken()) {
      return this.setIdentityProviderFromIssuer();
    }

    const identityProviderFromSession = sessionStorage.getItem('identityProvider');
    if (identityProviderFromSession) {
      return this.setIdentityProvider(identityProviderFromSession as IdentityProvider);
    }

    const useOkta = this.featureFlagService.isFeatureEnabled('loyalty-gift-cards-okta');

    if (!useOkta) return this.setIdentityProvider(IdentityProvider.IdentityServer4);

    const clientId =
      idpCookie === IdentityProvider.Okta
        ? environment.zipAuth0.clientId
        : environment.zipIdp.clientId;

    const params: Record<string, string> = {
      identityProvider: idpCookie,
      clientId,
      customerId: this.customerId as string,
    };

    try {
      const response = await this.ngHttp
        .get<GetIdentityProviderResponse>(`${environment.apiHost}/auth/identity-provider`, {
          params,
        })
        .toPromise();
      this.setIdentityProvider(response.data);
    } catch {
      this.setIdentityProvider(IdentityProvider.IdentityServer4);
    }
  }

  private initConfig() {
    switch (this.identityProvider) {
      case IdentityProvider.Okta:
        this.config = {
          issuer: `${environment.zipAuth0.issuer}/`,
          redirectUri: `${window.location.origin}/au/giftcards/`,
          clientId: environment.zipAuth0.clientId,
          responseType: 'code',
          scope: 'openid profile offline_access',
          customQueryParams: {
            audience: environment.zipAuth0.audience,
          },
        };
        break;
      default:
        this.config = {
          issuer: environment.zipIdp.issuer,
          redirectUri: `${window.location.origin}/au/giftcards/`,
          clientId: environment.zipIdp.clientId,
          responseType: 'code',
          scope: 'openid profile offline_access',
        };
        break;
    }
  }
}
